import globalWindow from '@shared/core/globals';
import { useResponsive } from '../hooks/useResponsive';
import type { MediaScreen } from './DefaultMediaMatchProvider';
import { useDefaultMediaMatchContext } from './DefaultMediaMatchProvider';

/**
 * Media screen detection utilities based on responsive breakpoints.
 *
 * Breakpoint system:
 * - Mobile: ≤ 1024px
 * - Tablet: > 1024px and < 1280px
 * - Desktop: ≥ 1280px
 *
 * Note: The defaultBreakpointWidths array [480, 768, 1024, 1440, 1920] defines
 * the available breakpoint widths, but the actual screen type detection uses
 * the ranges defined above for mobile/tablet/desktop classification.
 */

const mediaScreenPrecedence: { [K in MediaScreen]: number } = {
  mobile: 0,
  tablet: 1,
  desktop: 2,
};

export interface UseMediaScreenParams {
  minScreenSize?: MediaScreen;
  maxScreenSize?: MediaScreen;
}

export const useMediaScreen = ({ minScreenSize = 'mobile', maxScreenSize = 'desktop' }: UseMediaScreenParams): boolean => {
  const { defaultScreen } = useDefaultMediaMatchContext();
  const [mediaScreen] = useResponsive<MediaScreen>({ values: { mobile: 'mobile', tablet: 'tablet', desktop: 'desktop' } }, defaultScreen);
  const currentMediaScreenPrecedence = mediaScreenPrecedence[mediaScreen];

  return mediaScreenPrecedence[minScreenSize] <= currentMediaScreenPrecedence && currentMediaScreenPrecedence <= mediaScreenPrecedence[maxScreenSize];
};

/**
 * Hook that returns true when the current screen size is mobile.
 *
 * @returns {boolean} True when screen width is ≤ 1024px
 */
export const useIsMobileScreen = () => {
  return useMediaScreen({
    minScreenSize: 'mobile',
    maxScreenSize: 'mobile',
  });
};

/**
 * Hook that returns true when the current screen size is tablet.
 *
 * @returns {boolean} True when screen width is > 1024px and < 1280px
 */
export const useIsTabletScreen = () => {
  return useMediaScreen({
    minScreenSize: 'tablet',
    maxScreenSize: 'tablet',
  });
};

/**
 * Hook that returns true when the current screen size is mobile or tablet.
 *
 * @returns {boolean} True when screen width is ≤ 1280px (mobile: ≤ 1024px, tablet: > 1024px and < 1280px)
 */
export const useIsMobileOrTablet = () => {
  return useMediaScreen({
    minScreenSize: 'mobile',
    maxScreenSize: 'tablet',
  });
};

export const useIsIOS = (): boolean => (globalWindow.navigator ? Boolean(navigator?.userAgent.match(/ipad|iphone/i)) : false);
